import { gql } from '@apollo/client';
import { Box } from '@mui/material';
import { useCarousel } from 'components/common/Media/components/Carousel/useCarousel';
import { SocialPostFragmentSocialMediaListeningPostCardCarouselFragment } from 'graphql/generated';
import { useEffect, useState } from 'react';
import { theme } from 'styles/theme';

gql`
  fragment SocialPostFragmentSocialMediaListeningPostCardCarousel on SocialPostModel {
    id
    carouselMedias {
      id
      type
      url
      __typename
    }
    thumbnailUrl
  }
`;

interface SocialMediaListeningPostCardCarouselProps {
  post: SocialPostFragmentSocialMediaListeningPostCardCarouselFragment;
  onHeightSet?: (renderedHeight: number) => void;
  renderActionsBottom?: (isHovered: boolean) => React.ReactNode;
  renderActionsTopRight?: (isHovered: boolean) => React.ReactNode;
  renderActionsTopLeft?: (isHovered: boolean) => React.ReactNode;
}

export const SocialMediaListeningPostCardCarousel = ({
  post,
  onHeightSet,
  renderActionsBottom,
  renderActionsTopRight,
  renderActionsTopLeft,
}: SocialMediaListeningPostCardCarouselProps) => {
  const [isHovered, setIsHovered] = useState(false);

  const { renderCarousel, renderedHeight } = useCarousel({
    medias: post.carouselMedias,
    fallbackUrl: post.thumbnailUrl ?? '',
    componentsProps: {
      carousel: {
        componentsProps: {
          // Show navigation and pagination when hovered
          showNavigation: isHovered,
          showPagination: isHovered,
          container: {
            sx: {
              width: '100%',
              height: '100%',
              '& .swiper-pagination': {
                zIndex: 3,
                bottom: theme.spacing(10),
              },
            },
          },
        },
      },
    },
  });

  useEffect(() => {
    onHeightSet?.(renderedHeight);
  }, [renderedHeight, onHeightSet]);

  return (
    <Box
      position="relative"
      width="100%"
      height="100%"
      sx={{ paddingTop: '177.77%', overflow: 'hidden' }} // 56.25% for 16:9 aspect ratio
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      data-testid="static-image-container"
    >
      <Box
        position="absolute"
        top="0"
        left="0"
        width="100%"
        height="100%"
        sx={{
          borderRadius: 4,
          cursor: 'pointer',
          objectFit: 'cover',
          zIndex: 1,
        }}
      >
        {renderCarousel()}
      </Box>
      <Box
        position="absolute"
        top={theme.spacing(2)}
        right={theme.spacing(2)}
        data-testid="top-right-actions-container"
        sx={{
          left: theme.spacing(2),
          display: 'flex',
          justifyContent: 'space-between',
          gap: 2,
          alignItems: 'center',
          minHeight: 24,
          zIndex: 2,
        }}
      >
        <Box>{renderActionsTopLeft && renderActionsTopLeft(isHovered)}</Box>
        <Box>{renderActionsTopRight && renderActionsTopRight(isHovered)}</Box>
      </Box>
      {renderActionsBottom && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            width: '100%',
            px: 2,
            pb: 2,
            pt: 6,
            borderRadius: '0 0 16px 16px',
            zIndex: 2,

            // Linear gradient from bottom to top, black -> transparent
            backgroundImage:
              'linear-gradient(180deg, rgba(0, 0, 0, 0), rgba(0, 0, 0, 0.6))',
          }}
          data-testid="bottom-actions-container"
        >
          {renderActionsBottom(isHovered)}
        </Box>
      )}
    </Box>
  );
};
